import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, mediaUrl, apiErr } from "@/lib/api";
import { Field, Input, Select, Button, AddableSelect } from "@/lib/ui";

const UNITS = ["Box", "Piece", "Sq.ft", "Kg", "Set", "Pair", "Meter", "Bundle"];

function Uploader({ label, value, accept, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const isVideo = accept.includes("video");
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success(`${label} uploaded`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label} <span className="text-orange-600">*</span></p>
      <div onClick={() => ref.current.click()} className="flex aspect-video cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-orange-400">
        {busy ? <i className="fa-solid fa-spinner fa-spin text-slate-400" /> :
          value ? (isVideo ? <video src={mediaUrl(value)} className="h-full w-full object-cover" /> : <img src={mediaUrl(value)} className="h-full w-full object-cover" alt={label} />) :
            <span className="text-xs text-slate-400"><i className={`fa-solid ${isVideo ? "fa-video" : "fa-camera"} mr-1`} />Upload</span>}
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}

const EMPTY = { seller_id: "", category: "", product: "", size: "", quality: "", condition: "New",
  quantity: "", moq: "", primary_unit: "Box", secondary_unit: "", actual_rate: "", primary_rate: "",
  secondary_rate: "", catalogue_image: "", stock_image: "", stock_video: "", city: "", locality: "",
  manufacturer_stock: false, pan_india: false, allow_buyers: false, description: "", keywords: "" };

export default function AddProductAdmin() {
  const [form, setForm] = useState(EMPTY);
  const [sellers, setSellers] = useState([]);
  const [filters, setFilters] = useState({ categories: [], sizes: [], cities: [], localities: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/sellers").then(({ data }) => setSellers(data));
    api.get("/public/filters").then(({ data }) => setFilters(data));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v, ...(k === "city" ? { locality: "" } : {}) }));
  const addMaster = async (kind, name) => {
    await api.post(`/admin/master/${kind}`, { name });
    const { data } = await api.get("/public/filters");
    setFilters(data);
  };
  const discount = form.actual_rate && form.primary_rate && Number(form.actual_rate) > 0
    ? Math.max(0, Math.round(((Number(form.actual_rate) - Number(form.primary_rate)) / Number(form.actual_rate)) * 100)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.seller_id) { toast.error("Select a seller"); return; }
    if (!form.catalogue_image || !form.stock_image) { toast.error("2 images required"); return; }
    if (!form.category || !form.product || !form.size || !form.city || !form.locality) { toast.error("Fill all required fields"); return; }
    setLoading(true);
    try {
      await api.post("/admin/products", { ...form, quantity: Number(form.quantity) || 0, moq: Number(form.moq) || 0,
        actual_rate: Number(form.actual_rate), primary_rate: Number(form.primary_rate), secondary_rate: Number(form.secondary_rate) || 0 });
      toast.success("Product added & published (approved)");
      setForm(EMPTY);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const locs = filters.localities.filter((l) => !form.city || l.city === form.city);

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Add Product (Admin)</h1>
      <p className="mt-1 text-sm text-slate-500">Create a listing on behalf of a seller. It goes live approved immediately.</p>
      <form onSubmit={submit} className="mt-5 space-y-6">
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
          <Field label="Seller" required>
            <Select value={form.seller_id} onChange={(e) => set("seller_id", e.target.value)} data-testid="apa-seller">
              <option value="">Select Seller</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.company_name || s.phone} ({s.seller_type})</option>)}
            </Select>
          </Field>
          <Field label="Category" required><AddableSelect testid="apa-category" addTestid="apa-category-add" value={form.category} placeholder="Select" options={filters.categories.map((c) => c.name)} onChange={(v) => set("category", v)} onAdd={(name) => addMaster("categories", name)} /></Field>
          <Field label="Product Name" required><Input value={form.product} onChange={(e) => set("product", e.target.value)} data-testid="apa-product" /></Field>
          <Field label="Size" required><Select value={form.size} onChange={(e) => set("size", e.target.value)}><option value="">Select</option>{filters.sizes.map((s) => <option key={s.id}>{s.name}</option>)}</Select></Field>
          <Field label="Quality"><Input value={form.quality} onChange={(e) => set("quality", e.target.value)} /></Field>
          <div className="sm:col-span-2">
            <Field label="Product Description">
              <textarea data-testid="apa-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" placeholder="Add a detailed description of the product…" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Search Keywords">
              <Input data-testid="apa-keywords" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} placeholder="Comma-separated: sofa, couch, wooden furniture" />
            </Field>
          </div>
          <Field label="Condition"><Select value={form.condition} onChange={(e) => set("condition", e.target.value)}><option>New</option><option>Used</option><option>Refurbished</option></Select></Field>
          <Field label="Quantity" required><Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
          <Field label="MOQ"><Input type="number" value={form.moq} onChange={(e) => set("moq", e.target.value)} /></Field>
          <Field label="Primary Unit"><Select value={form.primary_unit} onChange={(e) => set("primary_unit", e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</Select></Field>
          <Field label="Secondary Unit"><Select value={form.secondary_unit} onChange={(e) => set("secondary_unit", e.target.value)}><option value="">None</option>{UNITS.map((u) => <option key={u}>{u}</option>)}</Select></Field>
        </div>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-3">
          <Field label="Actual Rate (₹)" required><Input type="number" value={form.actual_rate} onChange={(e) => set("actual_rate", e.target.value)} /></Field>
          <Field label="Primary Rate (₹)" required><Input type="number" value={form.primary_rate} onChange={(e) => set("primary_rate", e.target.value)} /></Field>
          <Field label="Secondary Rate (₹)"><Input type="number" value={form.secondary_rate} onChange={(e) => set("secondary_rate", e.target.value)} disabled={!form.secondary_unit} /></Field>
          <div className="sm:col-span-3"><span className="inline-flex rounded-lg bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700">Auto Discount: {discount}% OFF</span></div>
        </div>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-3">
          <Uploader label="Catalogue Image" accept="image/*" value={form.catalogue_image} onChange={(v) => set("catalogue_image", v)} />
          <Uploader label="Original Stock Image" accept="image/*" value={form.stock_image} onChange={(v) => set("stock_image", v)} />
          <Uploader label="Real Stock Video" accept="video/*" value={form.stock_video} onChange={(v) => set("stock_video", v)} />
        </div>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
          <Field label="City" required><Select value={form.city} onChange={(e) => set("city", e.target.value)}><option value="">Select</option>{filters.cities.map((c) => <option key={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Locality" required><Select value={form.locality} onChange={(e) => set("locality", e.target.value)} disabled={!form.city}><option value="">Select</option>{locs.map((l) => <option key={l.id}>{l.name}</option>)}</Select></Field>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"><span className="font-semibold text-slate-800">Manufacturer Stock</span><input type="checkbox" checked={form.manufacturer_stock} onChange={(e) => set("manufacturer_stock", e.target.checked)} className="h-5 w-5" /></label>
          {form.manufacturer_stock && (
            <label className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/50 px-4 py-3 sm:col-span-2"><div><p className="font-semibold text-slate-800">Allow End-User (Buyer) Visibility</p><p className="text-xs text-slate-500">Also show to buyers in the public marketplace</p></div><input data-testid="apa-allowbuyers" type="checkbox" checked={form.allow_buyers} onChange={(e) => set("allow_buyers", e.target.checked)} className="h-5 w-5" /></label>
          )}
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"><span className="font-semibold text-slate-800">Sell Pan India</span><input type="checkbox" checked={form.pan_india} onChange={(e) => set("pan_india", e.target.checked)} className="h-5 w-5" /></label>
        </div>
        <Button type="submit" disabled={loading} className="w-full py-3 text-base" data-testid="apa-submit">{loading ? "Adding…" : "Add & Publish Product"}</Button>
      </form>
    </div>
  );
}
